'use client'

import { useEffect } from 'react'
import { useSearchParams } from 'next/navigation'
import { restFeathers } from '@/lib/feathersClient'

interface AffiliateData {
  partnerId: string
  timestamp: number
  expiresAt: number
}

const AFFILIATE_STORAGE_KEY = 'achieveapex_affiliate_data'
const AFFILIATE_VISITOR_KEY = 'achieveapex_affiliate_visitor_id'
const ATTRIBUTION_DAYS = 10
const ATTRIBUTION_DURATION = ATTRIBUTION_DAYS * 24 * 60 * 60 * 1000 // 10 days in milliseconds

// Stable anonymous id so repeat visits from the same browser can be deduped
// server-side without identifying the visitor.
const getVisitorId = (): string | null => {
  if (typeof window === 'undefined') return null

  try {
    const existing = localStorage.getItem(AFFILIATE_VISITOR_KEY)
    if (existing) return existing

    const visitorId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`

    localStorage.setItem(AFFILIATE_VISITOR_KEY, visitorId)
    return visitorId
  } catch (error) {
    console.error('[Partners:LinkVisit] Error resolving visitor id', error)
    return null
  }
}

export const useAffiliateTracking = () => {
  const searchParams = useSearchParams()

  // Get current affiliate data from localStorage
  const getAffiliateData = (): AffiliateData | null => {
    if (typeof window === 'undefined') return null
    
    try {
      const stored = localStorage.getItem(AFFILIATE_STORAGE_KEY)
      if (!stored) return null
      
      const data: AffiliateData = JSON.parse(stored)
      
      // Check if expired
      if (Date.now() > data.expiresAt) {
        localStorage.removeItem(AFFILIATE_STORAGE_KEY)
        return null
      }
      
      return data
    } catch (error) {
      console.error('Error reading affiliate data:', error)
      localStorage.removeItem(AFFILIATE_STORAGE_KEY)
      return null
    }
  }

  // Save affiliate data to localStorage
  const setAffiliateData = (partnerId: string) => {
    if (typeof window === 'undefined') return
    
    const now = Date.now()
    const affiliateData: AffiliateData = {
      partnerId,
      timestamp: now,
      expiresAt: now + ATTRIBUTION_DURATION
    }
    
    try {
      localStorage.setItem(AFFILIATE_STORAGE_KEY, JSON.stringify(affiliateData))
      console.log(`🔗 Affiliate tracking set: Partner ${partnerId} for ${ATTRIBUTION_DAYS} days`)
    } catch (error) {
      console.error('Error saving affiliate data:', error)
    }
  }

  // Clear affiliate data
  const clearAffiliateData = () => {
    if (typeof window === 'undefined') return
    localStorage.removeItem(AFFILIATE_STORAGE_KEY)
  }

  // Get current valid partner ID
  const getCurrentPartnerId = (): string | null => {
    const data = getAffiliateData()
    return data?.partnerId || null
  }

  // Track partner from URL parameters
  useEffect(() => {
    if (typeof window === 'undefined') return
    
    const partner = searchParams.get('partner')
    
    if (partner) {
      // Always update with new partner (overrides existing)
      setAffiliateData(partner)

      // Record the visit so the internal partners analytics can measure how
      // many people actually land on each partner link, not just how many
      // end up signing up.
      const visitorId = getVisitorId()
      if (visitorId) {
        restFeathers
          .service('partner-link-visits')
          .create({
            partner_organization_id: parseInt(partner, 10),
            visitor_id: visitorId,
            landing_path: window.location.pathname,
            referrer: document.referrer || undefined,
            utm_source: searchParams.get('utm_source') || undefined,
            utm_medium: searchParams.get('utm_medium') || undefined,
            utm_campaign: searchParams.get('utm_campaign') || undefined
          })
          .catch((error: unknown) => {
            console.error('[Partners:LinkVisit] Failed to record affiliate link visit', error)
          })
      }
    }
  }, [searchParams])

  return {
    getCurrentPartnerId,
    getAffiliateData,
    setAffiliateData,
    clearAffiliateData
  }
}
